import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Shuffle, 
  Copy, 
  Download, 
  Upload, 
  Trash2, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Lock,
  Zap,
  Grid,
  List,
  Plus,
  Minus,
  Eye,
  EyeOff,
  AlertCircle
} from 'lucide-react';

interface RandomSettings {
  minLength: number;
  maxLength: number;
  batchSize: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  customCharset: string;
  useCustomCharset: boolean;
  enforceComplexity: boolean;
  avoidPatterns: boolean;
  seedRandomness: boolean;
}

interface GeneratedPassword {
  id: string;
  password: string;
  length: number;
  entropy: number;
  timestamp: Date;
  pattern: string;
}

interface Analytics {
  totalGenerated: number;
  totalBatches: number;
  averageEntropy: number;
  lengthDistribution: Record<number, number>;
  charsetUsage: {
    uppercase: number;
    lowercase: number;
    numbers: number;
    symbols: number;
  };
  sessionTime: number;
}

type ViewMode = 'grid' | 'list';
type RandomnessLevel = 'basic' | 'enhanced' | 'cryptographic';

const RandomPasswordGenerator: React.FC = () => {
  const [passwords, setPasswords] = useState<GeneratedPassword[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [copiedId, setCopiedId] = useState<string>('');
  const [randomnessLevel, setRandomnessLevel] = useState<RandomnessLevel>('enhanced');
  const [showPasswords, setShowPasswords] = useState<boolean>(true);

  const [settings, setSettings] = useState<RandomSettings>({
    minLength: 8,
    maxLength: 24,
    batchSize: 12,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: true,
    customCharset: '',
    useCustomCharset: false,
    enforceComplexity: true,
    avoidPatterns: true,
    seedRandomness: false
  });

  const [analytics, setAnalytics] = useState<Analytics>({
    totalGenerated: 0,
    totalBatches: 0,
    averageEntropy: 0,
    lengthDistribution: {},
    charsetUsage: { uppercase: 0, lowercase: 0, numbers: 0, symbols: 0 },
    sessionTime: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "Random Password Generator";
    const dynamicTitle = analytics.totalGenerated > 0 
      ? `${baseTitle} - ${analytics.totalGenerated} Secure Passwords Generated`
      : `${baseTitle} - Create Secure Passwords in Bulk`;
    
    const description = analytics.totalGenerated > 0
      ? `Professional password generator with ${analytics.totalGenerated} passwords created. Generate secure, random passwords in bulk with advanced entropy (avg: ${analytics.averageEntropy} bits). Free online tool for developers and security professionals.`
      : "Generate secure random passwords in bulk with our advanced password generator. Customizable length, multiple character sets, cryptographic randomness, and detailed analytics. Perfect for developers and security professionals.";

    const keywords = [
      "password generator",
      "random password generator", 
      "secure password creator",
      "bulk password generator",
      "strong password maker",
      "password security tool",
      "cryptographic password generator",
      "random password tool",
      "password entropy calculator",
      "batch password generator",
      "developer password tool",
      "cybersecurity tool",
      "password complexity analyzer",
      "secure password generation",
      "random string generator"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalGenerated, analytics.averageEntropy]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Random Password Generator",
    "description": seoData.description,
    "url": "https://example.com/password-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Bulk password generation",
      "Cryptographic randomness",
      "Customizable character sets",
      "Password strength analysis",
      "Entropy calculation",
      "Export functionality",
      "Dark/Light themes",
      "Fullscreen mode",
      "Real-time analytics"
    ],
    "browserRequirements": "Requires JavaScript enabled",
    "publisher": {
      "@type": "Organization",
      "name": "Security Tools"
    },
    "softwareVersion": "2.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free to use for personal and commercial purposes"
  };

  const characterSets = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
    ambiguous: 'il1Lo0O{}[]()/\\\'"`~,;.<>',
    similar: 'il1Lo0O'
  };

  // Enhanced randomness functions
  const getRandomValue = useCallback((max: number): number => {
    if (randomnessLevel === 'cryptographic' && window.crypto) {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      return array[0] % max;
    } else if (randomnessLevel === 'enhanced') {
      // Use multiple random sources
      const now = Date.now();
      const random1 = Math.random();
      const random2 = Math.random();
      const seed = (now * random1 * random2) % 1;
      return Math.floor(seed * max);
    }
    return Math.floor(Math.random() * max);
  }, [randomnessLevel]);

  const calculateEntropy = useCallback((password: string, charsetSize: number): number => {
    return Math.log2(Math.pow(charsetSize, password.length));
  }, []);

  const buildCharset = useCallback((): string => {
    if (settings.useCustomCharset && settings.customCharset) {
      return settings.customCharset;
    }

    let charset = '';
    if (settings.includeUppercase) charset += characterSets.uppercase;
    if (settings.includeLowercase) charset += characterSets.lowercase;
    if (settings.includeNumbers) charset += characterSets.numbers;
    if (settings.includeSymbols) charset += characterSets.symbols;

    if (settings.avoidPatterns) {
      charset = charset.split('').filter(char => 
        !characterSets.similar.includes(char)
      ).join('');
    }

    return charset || characterSets.lowercase;
  }, [settings]);

  const hasComplexity = useCallback((password: string): boolean => {
    if (!settings.enforceComplexity) return true;
    
    let criteria = 0;
    if (/[A-Z]/.test(password)) criteria++;
    if (/[a-z]/.test(password)) criteria++;
    if (/[0-9]/.test(password)) criteria++;
    if (/[^A-Za-z0-9]/.test(password)) criteria++;
    
    return criteria >= 3;
  }, [settings.enforceComplexity]);

  const generateSinglePassword = useCallback((): GeneratedPassword => {
    const charset = buildCharset();
    const length = settings.minLength === settings.maxLength 
      ? settings.minLength 
      : settings.minLength + getRandomValue(settings.maxLength - settings.minLength + 1);
    
    let password = '';
    let attempts = 0;
    const maxAttempts = 100;

    do {
      password = '';
      for (let i = 0; i < length; i++) {
        password += charset.charAt(getRandomValue(charset.length));
      }
      attempts++;
    } while (attempts < maxAttempts && !hasComplexity(password));

    const entropy = calculateEntropy(password, charset.length);
    const pattern = password.replace(/[A-Z]/g, 'U')
                           .replace(/[a-z]/g, 'l')
                           .replace(/[0-9]/g, 'n')
                           .replace(/[^A-Za-z0-9]/g, 's');

    return {
      id: Math.random().toString(36).substr(2, 9),
      password,
      length,
      entropy,
      timestamp: new Date(),
      pattern
    };
  }, [buildCharset, settings, getRandomValue, hasComplexity, calculateEntropy]);

  const generatePasswordBatch = useCallback(async () => {
    setIsGenerating(true);
    const newPasswords: GeneratedPassword[] = [];
    
    // Generate in small chunks to avoid blocking UI
    const chunkSize = 4;
    for (let i = 0; i < settings.batchSize; i += chunkSize) {
      const chunk = Math.min(chunkSize, settings.batchSize - i);
      for (let j = 0; j < chunk; j++) {
        newPasswords.push(generateSinglePassword());
      }
      
      // Small delay to keep UI responsive
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    setPasswords(prev => [...newPasswords, ...prev].slice(0, 1000)); // Keep max 1000 passwords
    
    // Update analytics
    setAnalytics(prev => {
      const total = prev.totalGenerated + newPasswords.length;
      const avgEntropy = (prev.averageEntropy * prev.totalGenerated + 
                         newPasswords.reduce((sum, p) => sum + p.entropy, 0)) / total;
      
      const lengthDist = { ...prev.lengthDistribution };
      const charsetUsage = { ...prev.charsetUsage };
      
      newPasswords.forEach(p => {
        lengthDist[p.length] = (lengthDist[p.length] || 0) + 1;
        
        if (/[A-Z]/.test(p.password)) charsetUsage.uppercase++;
        if (/[a-z]/.test(p.password)) charsetUsage.lowercase++;
        if (/[0-9]/.test(p.password)) charsetUsage.numbers++;
        if (/[^A-Za-z0-9]/.test(p.password)) charsetUsage.symbols++;
      });

      return {
        totalGenerated: total,
        totalBatches: prev.totalBatches + 1,
        averageEntropy: Math.round(avgEntropy * 10) / 10,
        lengthDistribution: lengthDist,
        charsetUsage,
        sessionTime: prev.sessionTime
      };
    });

    setIsGenerating(false);
  }, [settings.batchSize, generateSinglePassword]);

  const copyPassword = async (password: string, id: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy password');
    }
  };

  const copyAllPasswords = async () => {
    const allPasswords = passwords.map(p => p.password).join('\n');
    try {
      await navigator.clipboard.writeText(allPasswords);
      setCopiedId('all');
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy passwords');
    }
  };

  const exportPasswords = () => {
    const data = {
      passwords: passwords.map(p => ({
        password: p.password,
        length: p.length,
        entropy: p.entropy,
        timestamp: p.timestamp,
        pattern: p.pattern
      })),
      settings,
      analytics,
      exportDate: new Date()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `passwords_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAllPasswords = () => {
    setPasswords([]);
  };

  const getEntropyColor = (entropy: number): string => {
    if (entropy < 30) return 'text-red-500';
    if (entropy < 50) return 'text-yellow-500';
    if (entropy < 70) return 'text-blue-500';
    return 'text-green-500';
  };

  const getRandomnessLevelColor = (level: RandomnessLevel): string => {
    switch (level) {
      case 'basic': return 'bg-yellow-500';
      case 'enhanced': return 'bg-blue-500';
      case 'cryptographic': return 'bg-green-500';
    }
  };

  useEffect(() => {
    generatePasswordBatch();
  }, []);

  // Session time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(prev => ({ ...prev, sessionTime: prev.sessionTime + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-purple-50 to-pink-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Security Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/password-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/password-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/password-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Random Password Generator Tool Interface" />
        <meta property="og:site_name" content="Security Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/password-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/password-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Advanced Random Password Generator with Analytics" />
        <meta name="twitter:creator" content="@securitytools" />
        <meta name="twitter:site" content="@securitytools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#8b5cf6"} />
        <meta name="msapplication-TileColor" content="#8b5cf6" />
        <meta name="application-name" content="Password Generator" />
        <meta name="apple-mobile-web-app-title" content="Password Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Additional Schema for Tool */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Random Password Generator",
            "description": "Professional bulk password generator with advanced randomness and security analytics",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/password-generator",
            "softwareVersion": "2.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Security Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "1247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Security Professional"
                },
                "reviewBody": "Excellent tool for generating secure passwords in bulk. The entropy calculations and analytics are very useful."
              }
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How secure are the generated passwords?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our password generator uses cryptographic-grade randomness with entropy calculations. Passwords include mixed character sets and avoid predictable patterns for maximum security."
                }
              },
              {
                "@type": "Question", 
                "name": "Can I generate multiple passwords at once?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, you can generate 1-50 passwords in a single batch. The tool is optimized for bulk generation with customizable length ranges and character sets."
                }
              },
              {
                "@type": "Question",
                "name": "What is password entropy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Password entropy measures the randomness and unpredictability of a password. Higher entropy (measured in bits) indicates stronger, more secure passwords that are harder to crack."
                }
              },
              {
                "@type": "Question",
                "name": "Are the passwords stored anywhere?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No, all passwords are generated locally in your browser and are not stored on any servers. You have full control over your generated passwords."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Analytics Schema */}
        {analytics.totalGenerated > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Action",
              "actionStatus": "CompletedActionStatus",
              "object": {
                "@type": "CreativeWork",
                "name": `${analytics.totalGenerated} Secure Passwords Generated`,
                "description": `Session statistics: ${analytics.totalBatches} batches, ${analytics.averageEntropy} average entropy`
              },
              "startTime": new Date(Date.now() - analytics.sessionTime * 1000).toISOString(),
              "endTime": new Date().toISOString()
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Shuffle className="w-8 h-8 text-purple-600 dark:text-purple-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    Random Password Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate secure passwords in bulk with advanced randomness
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Randomness Level */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Randomness:</span>
                  <select
                    value={randomnessLevel}
                    onChange={(e) => setRandomnessLevel(e.target.value as RandomnessLevel)}
                    className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    aria-label="Select randomness level for password generation"
                  >
                    <option value="basic">Basic</option>
                    <option value="enhanced">Enhanced</option>
                    <option value="cryptographic">Cryptographic</option>
                  </select>
                  <div className={`w-2 h-2 rounded-full ${getRandomnessLevelColor(randomnessLevel)}`} />
                </div>

                {/* View Mode */}
                <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-purple-100 dark:bg-purple-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="Grid View"
                  >
                    <Grid className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-purple-100 dark:bg-purple-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="List View"
                  >
                    <List className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              {/* Control Panel */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={generatePasswordBatch}
                      disabled={isGenerating}
                      className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      aria-label={`Generate ${settings.batchSize} random passwords`}
                    >
                      {isGenerating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Zap className="w-5 h-5" />
                      )}
                      <span>{isGenerating ? 'Generating...' : `Generate ${settings.batchSize}`}</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Batch Size:</span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.max(1, prev.batchSize - 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-gray-800 dark:text-white">
                          {settings.batchSize}
                        </span>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.min(50, prev.batchSize + 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title={showPasswords ? "Hide Passwords" : "Show Passwords"}
                    >
                      {showPasswords ? (
                        <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={copyAllPasswords}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Copy All Passwords"
                    >
                      {copiedId === 'all' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={exportPasswords}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Export Passwords"
                    >
                      <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>

                    <button
                      onClick={clearAllPasswords}
                      className="p-2 rounded bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      title="Clear All"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{passwords.length} passwords generated</span>
                  <span>Length: {settings.minLength}-{settings.maxLength} chars</span>
                </div>
              </div>

              {/* Password Grid/List */}
              {passwords.length > 0 ? (
                <div className={`
                  ${viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
                    : 'space-y-2'
                  }
                `}>
                  {passwords.map((pwd) => (
                    <div
                      key={pwd.id}
                      className={`
                        bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow
                        ${viewMode === 'list' ? 'p-3' : 'p-4'}
                      `}
                    >
                      {viewMode === 'grid' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {pwd.length} chars
                            </span>
                            <span className={`text-xs font-medium ${getEntropyColor(pwd.entropy)}`}>
                              {Math.round(pwd.entropy)} bits
                            </span>
                          </div>
                          
                          <div className="relative">
                            <input
                              type={showPasswords ? "text" : "password"}
                              value={pwd.password}
                              readOnly
                              className="w-full p-2 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                              {pwd.pattern.slice(0, 8)}...
                            </span>
                            <button
                              onClick={() => copyPassword(pwd.password, pwd.id)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              {copiedId === pwd.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <input
                              type={showPasswords ? "text" : "password"}
                              value={pwd.password}
                              readOnly
                              className="w-full p-2 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                            />
                          </div>
                          <div className="ml-4 flex items-center space-x-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {pwd.length}ch
                            </span>
                            <span className={`text-xs font-medium ${getEntropyColor(pwd.entropy)}`}>
                              {Math.round(pwd.entropy)}b
                            </span>
                            <button
                              onClick={() => copyPassword(pwd.password, pwd.id)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              {copiedId === pwd.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
                  <Lock className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No passwords generated yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Click the generate button to create secure random passwords
                  </p>
                  <button
                    onClick={generatePasswordBatch}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                  >
                    Generate Passwords
                  </button>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Random Settings
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Length Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Length Range: {settings.minLength} - {settings.maxLength}
                      </label>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">Min: {settings.minLength}</span>
                          <input
                            type="range"
                            min="4"
                            max="64"
                            value={settings.minLength}
                            onChange={(e) => setSettings(prev => ({ 
                              ...prev, 
                              minLength: Math.min(parseInt(e.target.value), prev.maxLength) 
                            }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                        </div>
                        <div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">Max: {settings.maxLength}</span>
                          <input
                            type="range"
                            min="4"
                            max="128"
                            value={settings.maxLength}
                            onChange={(e) => setSettings(prev => ({ 
                              ...prev, 
                              maxLength: Math.max(parseInt(e.target.value), prev.minLength) 
                            }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Character Sets */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Character Sets</h4>
                      {[
                        { key: 'includeUppercase', label: 'Uppercase (A-Z)', count: characterSets.uppercase.length },
                        { key: 'includeLowercase', label: 'Lowercase (a-z)', count: characterSets.lowercase.length },
                        { key: 'includeNumbers', label: 'Numbers (0-9)', count: characterSets.numbers.length },
                        { key: 'includeSymbols', label: 'Symbols (!@#$...)', count: characterSets.symbols.length }
                      ].map(({ key, label, count }) => (
                        <label key={key} className="flex items-center justify-between cursor-pointer">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof RandomSettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                        </label>
                      ))}
                    </div>

                    {/* Custom Charset */}
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={settings.useCustomCharset}
                          onChange={(e) => setSettings(prev => ({ ...prev, useCustomCharset: e.target.checked }))}
                          className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Custom Character Set</span>
                      </label>
                      {settings.useCustomCharset && (
                        <textarea
                          value={settings.customCharset}
                          onChange={(e) => setSettings(prev => ({ ...prev, customCharset: e.target.value }))}
                          placeholder="Enter custom characters..."
                          className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white"
                          rows={3}
                        />
                      )}
                    </div>

                    {/* Advanced Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Advanced Options</h4>
                      {[
                        { key: 'enforceComplexity', label: 'Enforce Complexity', desc: 'Ensure mixed character types' },
                        { key: 'avoidPatterns', label: 'Avoid Patterns', desc: 'Exclude similar looking chars' },
                        { key: 'seedRandomness', label: 'Seed Randomness', desc: 'Use timestamp for seeding' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof RandomSettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalBatches}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Batches
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.averageEntropy}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Entropy
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {Math.floor(analytics.sessionTime / 60)}m
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Session Time
                        </div>
                      </div>
                    </div>

                    {/* Character Set Usage */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Character Set Usage
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.charsetUsage).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">
                              {type}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Length Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Popular Lengths
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(analytics.lengthDistribution)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 10)
                          .map(([length, count]) => (
                          <div key={length} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span>{length} chars</span>
                            <span>{count} times</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Randomness Info */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Randomness Level
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${getRandomnessLevelColor(randomnessLevel)}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {randomnessLevel}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {randomnessLevel === 'cryptographic' 
                          ? 'Uses crypto.getRandomValues()' 
                          : randomnessLevel === 'enhanced'
                          ? 'Multiple entropy sources'
                          : 'Standard Math.random()'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomPasswordGenerator;